import type { RefObject } from 'react'

import {
    gsap,
    ScrollTrigger,
    SplitText,
    useGSAP,
    prefersReducedMotion
} from '@/lib/gsap'
import { onStageReady } from '@/lib/stage'

/**
 * Declarative scroll animations. Add these attributes to any element inside
 * the page layout and they animate in when scrolled into view:
 *
 * data-split            headings: lines slide up from a mask
 * data-reveal           "up" (default), "fade", "scale", "line" or "image"
 * data-reveal-group     direct children rise in one after another
 * data-parallax="12"    drifts vertically while the section scrolls by
 * data-words            statement text that brightens word by word on scroll
 * data-count="62"       numbers count up (render with the Counter component)
 * data-delay="0.2"      optional delay, in seconds, for split and reveal
 */

export const formatCount = (value: number, decimals = 0, prefix = '', suffix = ''): string => {
    const formatted = value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    })

    return `${prefix}${formatted}${suffix}`
}

const setupReveals = (root: HTMLElement): void => {
    const select = (selector: string): HTMLElement[] => gsap.utils.toArray<HTMLElement>(root.querySelectorAll(selector))

    select('[data-split]').forEach((element) => {
        const delay = Number(element.dataset.delay ?? 0)

        SplitText.create(element, {
            type: 'lines',
            mask: 'lines',
            linesClass: 'split-line',
            autoSplit: true,
            onSplit: (self) => gsap.from(self.lines, {
                yPercent: 112,
                duration: 1.25,
                ease: 'expo.out',
                stagger: 0.085,
                delay,
                scrollTrigger: {
                    trigger: element,
                    start: 'top 90%',
                    once: true
                }
            })
        })
    })

    select('[data-reveal]').forEach((element) => {
        const type = element.dataset.reveal || 'up'
        const delay = Number(element.dataset.delay ?? 0)
        const scrollTrigger = {
            trigger: element,
            start: 'top 90%',
            once: true
        }

        if (type === 'image') {
            const inner = element.querySelector('[data-reveal-inner]') ?? element.querySelector('img')
            const timeline = gsap.timeline({ scrollTrigger, delay })

            timeline.from(element, {
                clipPath: 'inset(100% 0% 0% 0%)',
                duration: 1.5,
                ease: 'expo.inOut'
            })

            if (inner) {
                timeline.from(inner, {
                    scale: 1.3,
                    duration: 1.9,
                    ease: 'expo.out'
                }, 0.15)
            }

            return
        }

        if (type === 'line') {
            gsap.from(element, {
                scaleX: 0,
                transformOrigin: 'left center',
                duration: 1.4,
                ease: 'expo.inOut',
                delay,
                scrollTrigger
            })

            return
        }

        if (type === 'fade') {
            gsap.from(element, {
                autoAlpha: 0,
                duration: 1.2,
                ease: 'power2.out',
                delay,
                scrollTrigger
            })

            return
        }

        if (type === 'scale') {
            gsap.from(element, {
                autoAlpha: 0,
                scale: 0.94,
                duration: 1.3,
                ease: 'expo.out',
                delay,
                scrollTrigger
            })

            return
        }

        gsap.from(element, {
            y: 44,
            autoAlpha: 0,
            duration: 1.15,
            ease: 'expo.out',
            delay,
            scrollTrigger
        })
    })

    select('[data-reveal-group]').forEach((group) => {
        const stagger = Number(group.dataset.revealGroup || 0.08)

        gsap.from(Array.from(group.children), {
            y: 36,
            autoAlpha: 0,
            duration: 1.1,
            ease: 'expo.out',
            stagger,
            scrollTrigger: {
                trigger: group,
                start: 'top 88%',
                once: true
            }
        })
    })

    select('[data-parallax]').forEach((element) => {
        const amount = Number(element.dataset.parallax || 10)

        gsap.fromTo(element, { yPercent: -amount }, {
            yPercent: amount,
            ease: 'none',
            scrollTrigger: {
                trigger: element.parentElement ?? element,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true
            }
        })
    })

    select('[data-words]').forEach((element) => {
        SplitText.create(element, {
            type: 'words',
            autoSplit: true,
            onSplit: (self) => gsap.fromTo(self.words, { opacity: 0.16 }, {
                opacity: 1,
                ease: 'none',
                stagger: 0.12,
                scrollTrigger: {
                    trigger: element,
                    start: 'top 82%',
                    end: 'bottom 52%',
                    scrub: true
                }
            })
        })
    })

    select('[data-count]').forEach((element) => {
        const target = Number(element.dataset.count)
        const decimals = Number(element.dataset.decimals ?? 0)
        const prefix = element.dataset.prefix ?? ''
        const suffix = element.dataset.suffix ?? ''
        const state = { value: 0 }

        if (Number.isNaN(target)) return

        element.textContent = formatCount(0, decimals, prefix, suffix)

        gsap.to(state, {
            value: target,
            duration: 2.1,
            ease: 'power3.out',
            scrollTrigger: {
                trigger: element,
                start: 'top 92%',
                once: true
            },
            onUpdate: () => {
                element.textContent = formatCount(state.value, decimals, prefix, suffix)
            },
            onComplete: () => {
                element.textContent = formatCount(target, decimals, prefix, suffix)
            }
        })
    })
}

export const useReveals = (scope: RefObject<HTMLElement | null>, key: string): void => {
    useGSAP((_context, contextSafe) => {
        const root = scope.current

        if (!root || !contextSafe || prefersReducedMotion()) return

        const setup = contextSafe(() => {
            setupReveals(root)

            requestAnimationFrame(() => {
                ScrollTrigger.sort()
                ScrollTrigger.refresh()
            })
        })

        return onStageReady(setup)
    }, { scope, dependencies: [key], revertOnUpdate: true })
}
